/** Společná logika časové osy pro Gantt (globální přehled i detail polygonu). */

const MS_PER_DAY = 24 * 60 * 60 * 1000;

export interface GanttMonthSlice {
  label: string;
  left: number;
  width: number;
}

export function startOfDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

export function addDays(date: Date, days: number): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate() + days);
}

export function daysBetween(a: Date, b: Date): number {
  return Math.round((startOfDay(b).getTime() - startOfDay(a).getTime()) / MS_PER_DAY);
}

/** Parsování `dd.mm.yyyy` nebo ISO prefix `yyyy-mm-dd` (shodně s gantt / specifikací). */
export function parsePolygonScheduleDate(value: unknown): Date | null {
  if (typeof value !== 'string' || !value.trim()) return null;
  const text = value.trim();
  const cs = /^(\d{1,2})\.(\d{1,2})\.(\d{4})$/.exec(text);
  if (cs) return validDate(Number(cs[3]), Number(cs[2]), Number(cs[1]));
  const iso = /^(\d{4})-(\d{2})-(\d{2})/.exec(text);
  if (iso) return validDate(Number(iso[1]), Number(iso[2]), Number(iso[3]));
  return null;
}

function validDate(year: number, month: number, day: number): Date | null {
  const date = new Date(year, month - 1, day);
  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) return null;
  return startOfDay(date);
}

export function formatCsDate(date: Date): string {
  return `${String(date.getDate()).padStart(2, '0')}.${String(date.getMonth() + 1).padStart(2, '0')}.${date.getFullYear()}`;
}

export function buildGanttMonthSlices(timelineStart: Date, timelineEnd: Date): GanttMonthSlice[] {
  const totalDays = Math.max(1, daysBetween(timelineStart, timelineEnd));
  const months: GanttMonthSlice[] = [];
  let cursor = new Date(timelineStart.getFullYear(), timelineStart.getMonth(), 1);
  while (cursor <= timelineEnd) {
    const monthStart = new Date(Math.max(cursor.getTime(), timelineStart.getTime()));
    const nextMonth = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1);
    const monthEnd = new Date(Math.min(nextMonth.getTime(), timelineEnd.getTime()));
    months.push({
      label: cursor.toLocaleDateString('cs-CZ', { month: 'short', year: 'numeric' }),
      left: daysBetween(timelineStart, monthStart) / totalDays * 100,
      width: Math.max(2, daysBetween(monthStart, monthEnd) / totalDays * 100),
    });
    cursor = nextMonth;
  }
  return months;
}

/** Rozsah osy: min/max z dat + okraje ve dnech. */
export function computeTimelineRange(
  dates: Date[],
  opts: { padBefore: number; padAfter: number; fallbackDays: number },
): { start: Date; end: Date } {
  const today = startOfDay(new Date());
  const all = dates.length > 0 ? [...dates, today] : [today];
  const minT = Math.min(...all.map((d) => d.getTime()));
  const maxT = Math.max(...all.map((d) => d.getTime()));
  if (dates.length === 0) {
    return {
      start: addDays(today, -opts.padBefore),
      end: addDays(today, opts.fallbackDays),
    };
  }
  return {
    start: addDays(new Date(minT), -opts.padBefore),
    end: addDays(new Date(maxT), opts.padAfter),
  };
}
